import isNumber from 'lodash/isNumber'
import isUndefined from 'lodash/isUndefined'

import { ColumnSettings, ITableColumn } from '@makyoapp/makyo-sdk/models/Table'

import { Cell, ICell } from '.'

export type Separator = 'comma' | 'dot' | 'space' | 'none'

export interface INumberColumnSettings extends ColumnSettings {
  numAbbreviate: boolean
  numDecimalSeparator: Separator
  numPrecision: string
  numPrefix: string
  numSuffix: string
  numThousandSeparator: Separator
}

export interface INumberColumn extends ITableColumn {
  Settings: INumberColumnSettings
}

export interface INumberCell extends ICell {
  settings: INumberColumnSettings
}

export class NumberCell implements INumberCell {
  value: Cell
  settings: INumberColumnSettings

  constructor(value: Cell, settings: INumberColumnSettings) {
    this.value = parseNumberValue(value)
    this.settings = {
      numAbbreviate: settings?.numAbbreviate,
      numDecimalSeparator: settings.numDecimalSeparator,
      numPrecision: settings.numPrecision,
      numPrefix: settings.numPrefix,
      numSuffix: settings.numSuffix,
      numThousandSeparator: settings.numThousandSeparator,
    }
  }

  toString(): string {
    if (typeof this.value !== 'number' || this.value === null) {
      return ''
    }

    let num = this.value

    // Handle precision
    let precision = isUndefined(this.settings.numPrecision)
      ? undefined
      : Math.abs(Math.log10(parseFloat(this.settings.numPrecision))) || 0

    let numStr = isUndefined(precision) ? num.toString() : num.toFixed(precision)

    // Handle decimal separator
    if (this.settings.numDecimalSeparator !== 'dot') {
      const decimalSeparator = this.settings.numDecimalSeparator === 'comma' ? ',' : '.'
      numStr = numStr.replace('.', decimalSeparator)
    }

    // Handle thousand separator
    if (this.settings.numThousandSeparator !== 'none') {
      const thousandSeparator =
        this.settings.numThousandSeparator === 'comma' ? ',' : this.settings.numThousandSeparator === 'dot' ? '.' : ' '
      const parts = numStr.split(this.settings.numDecimalSeparator === 'comma' ? ',' : '.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandSeparator)
      numStr = parts.join(this.settings.numDecimalSeparator === 'comma' ? ',' : '.')
    }

    // Handle abbreviation
    if (this.settings?.numAbbreviate) {
      const abbreviations = ['', 'K', 'M', 'B', 'T']
      let index = 0
      while (num >= 1000 && index < abbreviations.length - 1) {
        num /= 1000
        index++
      }
      numStr = num.toFixed(precision) + abbreviations[index]
    }

    let prefix = this.settings.numPrefix ?? ''
    let suffix = this.settings.numSuffix ?? ''

    // Add prefix and suffix
    numStr = `${prefix}${numStr}${suffix}`

    return numStr
  }

  toCell<T extends Cell>(newValue: T) {
    this.value = parseNumberValue(newValue)
    return this
  }
}

export function parseNumberValue<T>(value: T): T {
  let finalValue: any = value
  if (typeof value === 'undefined' || value === null) {
    finalValue = null
  } else if (typeof value === 'string') {
    let numVal = parseFloat(value)
    if (isNaN(numVal)) {
      finalValue = null
    } else if (isNumber(numVal)) {
      finalValue = numVal
    } else {
      finalValue = null
    }
  }
  return finalValue
}
